import { GUID, IElement } from "@faro-lotv/ielement-types";
import { LightweightIElement } from "@faro-lotv/service-wires";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type LightweightElement = LightweightIElement & {
  childrenIds?: GUID[] | null;
};

export type LightweightElementsRecord = Record<
  GUID,
  LightweightElement | undefined
>;

export type LightweightElementsState = {
  /** A map GUID->element for all lightweight elements */
  lightweightElements: LightweightElementsRecord;

  /** A list of iElements that should be detached from the lightweight elements record */
  hiddenElements: LightweightElement[];
};

export type LightweightElementsStore = {
  /** The store slice defined here */
  lightweightElements: LightweightElementsState;
};

export type MoveElementArgs = {
  /** The id of the element to move */
  id: GUID;
  /**
   * The new parent of the element in the project structure.
   * If not provided, the element will just change order under the same parent.
   */
  newParentId?: GUID;
  /**
   * The element before which the element will be placed in the children list of the new parent.
   * If not provided, the element will be placed at the end.
   */
  nextSibling?: GUID;
};

const initialState: LightweightElementsState = {
  lightweightElements: {},
  hiddenElements: [],
};

const lightweightElementsSlice = createSlice({
  initialState,
  name: "lightweightElements",
  reducers: {
    addLightweightElements(
      state,
      action: PayloadAction<LightweightIElement[]>,
    ) {
      for (const el of action.payload) {
        state.lightweightElements[el.id] = el;
      }

      // rebuilding the childrenIds for all elements
      // TODO: this computation may be optimized by e.g. computing the children of only the
      // newly added lightweight elements. https://faro01.atlassian.net/browse/SWEB-7464
      const elements = Object.values(state.lightweightElements);
      for (const el of elements) {
        if (!el?.parentId) continue;
        const parent = state.lightweightElements[el.parentId];
        if (!parent) continue;
        const { id } = el;
        if (!parent.childrenIds) parent.childrenIds = [];
        if (!parent.childrenIds.includes(id)) parent.childrenIds.push(id);
      }
      hideLightweightElementsImpl(
        state,
        state.hiddenElements.map((e) => e.id),
      );
    },

    addLightweightElementsWithChildren(
      state,
      action: PayloadAction<LightweightElement[]>,
    ) {
      for (const el of action.payload) {
        state.lightweightElements[el.id] = el;
      }
      hideLightweightElementsImpl(
        state,
        state.hiddenElements.map((e) => e.id),
      );
    },

    moveLightweightElement(state, { payload }: PayloadAction<MoveElementArgs>) {
      const { id, newParentId, nextSibling } = payload;

      const element = state.lightweightElements[id];

      // element might be undefined in case of `id` being invalid
      if (!element) {
        console.warn(`LightweightElement with id ${id} not found`);
        return;
      }

      const oldParent = element.parentId
        ? state.lightweightElements[element.parentId]
        : undefined;

      // newParentId might be undefined if we are only moving the position of the element with respect to
      // its siblings
      const newParent = newParentId
        ? state.lightweightElements[newParentId]
        : oldParent;
      // newParent might be undefined in case of `newParentId` being invalid
      if (!newParent) {
        console.warn(`LightweightElement with id ${newParentId} not found`);
        return;
      }

      if (oldParent) {
        oldParent.childrenIds = oldParent.childrenIds
          ? oldParent.childrenIds.filter((existingId) => existingId !== id)
          : [];
      }

      if (!newParent.childrenIds?.length) {
        newParent.childrenIds = [];
      }

      if (nextSibling) {
        const siblingIndex = newParent.childrenIds.findIndex(
          (id) => id === nextSibling,
        );
        if (siblingIndex >= 0 && siblingIndex < newParent.childrenIds.length) {
          newParent.childrenIds.splice(siblingIndex, 0, id);
        }
      } else {
        newParent.childrenIds = [...newParent.childrenIds, id];
      }

      element.parentId = newParent.id;
    },

    removeLightweightElement(state, action: PayloadAction<GUID>) {
      const element = state.lightweightElements[action.payload];
      if (!element) return;

      // remove reference of this element from its parent
      const parent = state.lightweightElements[element.parentId ?? ""];

      if (parent) {
        parent.childrenIds =
          parent.childrenIds?.filter((x) => x !== element.id) ?? undefined;
      }

      // Remove this element and all its children from the store
      function recursiveRemoveIElement(id: GUID): void {
        const el = state.lightweightElements[id];

        if (!el) return;

        const children = el.childrenIds ?? [];
        for (const child of children) {
          recursiveRemoveIElement(child);
        }
        delete state.lightweightElements[id];
      }
      recursiveRemoveIElement(action.payload);
    },

    hideLightweightElements(state, { payload }: PayloadAction<GUID[]>) {
      hideLightweightElementsImpl(state, payload);
    },
  },
});

export const {
  addLightweightElements,
  addLightweightElementsWithChildren,
  moveLightweightElement,
  removeLightweightElement,
  hideLightweightElements,
} = lightweightElementsSlice.actions;

export const lightweightElementsReducer = lightweightElementsSlice.reducer;

/**
 * Converts an IElement into a LightweightElement
 *
 * @param element The IElement to convert
 * @returns The lightweight element resulting from the conversion
 */
export function toLightweight(element: IElement): LightweightElement {
  return {
    id: element.id,
    name: element.name,
    type: element.type,
    typeHint: element.typeHint,
    parentId: element.parentId,
    createdAt: element.createdAt,
    labelIds: element.labelIds ? [...element.labelIds] : undefined,
    childrenIds: element.childrenIds ? [...element.childrenIds] : undefined,
  };
}

function hideLightweightElementsImpl(
  state: LightweightElementsState,
  elements: GUID[],
): void {
  for (const hiddenElement of state.hiddenElements) {
    state.lightweightElements[hiddenElement.id] = hiddenElement;
  }

  const hiddenElements: LightweightElement[] = [];
  for (const iElementId of elements) {
    const iElement = state.lightweightElements[iElementId];
    if (!iElement) continue;

    hiddenElements.push(iElement);

    state.lightweightElements[iElementId] = undefined;
  }
  state.hiddenElements = hiddenElements;
}
